/**
 * ObjectTab — configuration panel for quantitative objects (Accounts, Contacts, Deals).
 *
 * This component renders the lovable Mapping-Studio design while composing the
 * existing API-connected sub-components (FieldMappingTable, RoleMappingTable,
 * WriteBackConfiguration) so that all backend wiring is preserved.
 */
import { Loader, Select, Switch, Text } from "@mantine/core";
import { toast } from "sonner";
import { IconInfoCircle, IconLink, IconRefresh } from "@tabler/icons-react";
import { useSyncObjectFields } from "../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import { RadioGroup, RadioGroupItem } from "../../../../components/ui/radio-group";
import { Button as DsButton } from "../../../../components/ui/button";

import FieldMappingTable from "../../hubspot/new-flow/components/FieldMappingTable";
import RoleMappingTable from "../../hubspot/new-flow/components/RoleMappingTable";
import RelationshipMappingTable from "../../hubspot/new-flow/components/RelationshipMappingTable";
import WriteBackConfiguration from "../../hubspot/new-flow/components/WriteBackConfiguration";

import type {
  ObjectConfig,
  SyncMode,
  HubSpotField,
  FieldDefinition,
} from "./types";
import { EXTERNAL_ID_FIELDS } from "./types";

import type {
  DraftFieldMapping,
  ConfigByObject,
  WritebackByObject,
  RoleMappingsByObject,
  RelationshipMappingsByObject,
  DraftMappingsByObject,
  WritebackFieldMapping,
} from "../../hubspot/new-flow/types";
import { useState } from "react";

// ─── Props ──────────────────────────────────────────────────────────────────

export interface ObjectTabProps {
  object: ObjectConfig;
  integrationName: string;
  providerKey: string;
  integrationId?: string;
  connectionId?: string;
  isConnected: boolean;
  onUpdate: (updates: Partial<ObjectConfig>) => void;

  // Existing API-connected state from ProviderNewFlow
  syncId?: string;
  writebackSyncId?: string;
  workspaceObjectId?: string;
  draftMappings: DraftMappingsByObject;
  writeback: WritebackByObject;
  roleMappingsState: RoleMappingsByObject;
  relationshipMappingsState: RelationshipMappingsByObject;
  config: ConfigByObject;

  // Callbacks to parent (ProviderNewFlow state management)
  onConfigPatch: (slug: string, patch: Partial<ConfigByObject[string]>) => void;
  onMappingsChange: (updater: (prev: DraftMappingsByObject) => DraftMappingsByObject) => void;
  onRoleMappingsChange: (updater: (prev: RoleMappingsByObject) => RoleMappingsByObject) => void;
  onRelationshipMappingsChange: (updater: (prev: RelationshipMappingsByObject) => RelationshipMappingsByObject) => void;
  onWritebackChange?: (updater: (prev: WritebackByObject) => WritebackByObject) => void;
  writebackMappingsState?: Record<string, WritebackFieldMapping[]>;
  onWritebackMappingsChange?: (
    slug: string,
    mappings: WritebackFieldMapping[],
    source?: "hydrate" | "user",
  ) => void;
  onToggleWriteback: (slug: string, enabled: boolean) => Promise<void> | void;
  onCreateSync?: (slug: string, sourceCollection?: string) => Promise<string | undefined>;
  onActivateSync?: (slug: string) => Promise<void>;
  onDeactivateSync?: (slug: string) => Promise<void>;
  persistenceMode?: "component" | "managed";
  reservedProviderFields?: Set<string>;
  fieldConflicts?: string[];

  // Provider fields (fetched from integration.app)
  providerFields: HubSpotField[];
  providerFieldsLoading: boolean;
  providerCollections: Array<{ key: string; name: string; fields: HubSpotField[] }>;
  selectedCollection: string | null;
  onCollectionChange: (collectionKey: string | null) => void;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function ObjectTab({
  object,
  integrationName,
  providerKey,
  integrationId,
  connectionId,
  isConnected,
  onUpdate,
  syncId,
  writebackSyncId,
  workspaceObjectId,
  draftMappings,
  writeback,
  roleMappingsState,
  relationshipMappingsState,
  config,
  onConfigPatch,
  onMappingsChange,
  onRoleMappingsChange,
  onRelationshipMappingsChange,
  onWritebackChange,
  writebackMappingsState = {},
  onWritebackMappingsChange,
  onToggleWriteback,
  onCreateSync,
  onActivateSync,
  onDeactivateSync,
  persistenceMode = "component",
  reservedProviderFields = new Set(),
  fieldConflicts = [],
  providerFields,
  providerFieldsLoading,
  providerCollections,
  selectedCollection,
  onCollectionChange,
}: ObjectTabProps) {
  const [togglingWriteback, setTogglingWriteback] = useState(false);
  const workspace: any = useWorkspaceStore((s: any) => s.workspace);
  const slug = object.key;
  const apiSlug = slug === "contacts" ? "users" : slug;
  const writebackConfigForObject = writeback[slug] || { enabled: false, allow_creates: false, allow_updates: true };
  const writebackEnabled = !!writebackConfigForObject.enabled;
  const writebackReady = persistenceMode === "managed" ? true : !!writebackSyncId;
  const conflictSet = new Set(fieldConflicts);
  const canRefreshProviderFields = Boolean(selectedCollection);
  const refreshProviderFields = () => {
    if (selectedCollection) {
      onCollectionChange(selectedCollection);
    }
  };
  const setWritebackEnabledLocally = (enabled: boolean) => {
    if (!onWritebackChange) return;
    onWritebackChange((prev) => ({
      ...prev,
      [slug]: {
        ...(prev[slug] || { allow_creates: false, allow_updates: true }),
        enabled,
      },
    }));
  };

  const handleWritebackEnabledChange = async (enabled: boolean) => {
    if (togglingWriteback) return;

    const previousEnabled = writebackEnabled;
    setTogglingWriteback(true);
    setWritebackEnabledLocally(enabled);

    if (persistenceMode === "managed") {
      setTogglingWriteback(false);
      return;
    }

    try {
      await Promise.resolve(onToggleWriteback(slug, enabled));
    } catch (error: any) {
      console.error(`Failed to ${enabled ? "enable" : "disable"} write-back:`, error);
      setWritebackEnabledLocally(previousEnabled);
      toast.error(`Failed to ${enabled ? "enable" : "disable"} sync-back`, {
        description: "Please try again.",
      });
    } finally {
      setTogglingWriteback(false);
    }
  };

  // Workspace fields for this object
  const { data: workspaceFields } = useSyncObjectFields({
    workspaceId: workspace?.id,
    objectSlug: apiSlug,
    enabled: !!workspace?.id && !!workspaceObjectId,
  });

  const externalIdFields = EXTERNAL_ID_FIELDS[integrationName]?.[slug] || EXTERNAL_ID_FIELDS.HubSpot?.[slug] || [];

  // ─── Disabled state ──────────────────────────────────────────────────────

  if (!object.enabled) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3 rounded-lg border border-solid border-[var(--mantine-color-default-border)] px-4 py-3">
          <Switch
            checked={false}
            onChange={(e) => onUpdate({ enabled: e.currentTarget.checked })}
          />
          <div>
            <Text size="sm">{object.label} sync with {integrationName} is disabled.</Text>
            <Text size="xs" c="dimmed">Click to enable.</Text>
          </div>
        </div>
      </div>
    );
  }

  // ─── Enabled state ───────────────────────────────────────────────────────

  const crmLabelSingular = object.crmLabel.split(" ")[1] || object.crmLabel;
  const configMatcherField = config[slug]?.matcher_field_name;
  const effectiveMatcherField = object.externalIdField || configMatcherField || "";
  const effectiveCustomMatcherField = effectiveMatcherField && effectiveMatcherField !== "external_id"
    ? effectiveMatcherField
    : "";
  const externalIdChoice = object.hasExternalId === true
    ? "yes"
    : object.hasExternalId === false
      ? "no"
      : effectiveCustomMatcherField
        ? "yes"
        : (config[slug]?.matching_method === "external_id" ? "no" : "");

  return (
    <div className="space-y-8">
      {/* Enable toggle */}
      <div className="flex items-center gap-3 rounded-lg border border-solid border-[color-mix(in_srgb,var(--mantine-primary-color-filled),transparent_80%)] bg-[color-mix(in_srgb,var(--mantine-primary-color-filled),transparent_97%)] px-4 py-3">
        <Switch
          checked
          onChange={(e) => onUpdate({ enabled: e.currentTarget.checked })}
        />
        <Text size="sm">
          {object.label} sync with {integrationName} is enabled. Click to disable.
        </Text>
      </div>

      {fieldConflicts.length > 0 && (
        <div className="rounded-[8px] border border-red-200 bg-red-50 px-3 py-2">
          <Text size="xs" c="red">
            Duplicate {integrationName} fields detected: {fieldConflicts.join(", ")}. Resolve duplicates before saving.
          </Text>
        </div>
      )}

      {/* ─── Section: Collection Selection ──────────────────────────────────── */}
      {providerCollections.length > 0 && (
        <div className="space-y-3">
          <Text size="sm">
            Select which {integrationName} collection to sync with Hyperengage {object.label}:
          </Text>
          <Select
            value={selectedCollection}
            onChange={onCollectionChange}
            data={providerCollections.map((c) => ({ value: c.key, label: c.name || c.key }))}
            placeholder={`Select ${integrationName} collection...`}
            searchable
            className="max-w-[400px]"
          />
        </div>
      )}

      {/* ─── Section: Sync mode ─────────────────────────────────────────────── */}
      <div className="space-y-3">
        <p className="text-[13px] leading-[1.45] text-[var(--mantine-color-text)]">
          Do you want to create new {object.label} in Hyperengage for {object.crmLabel}, or just
          update current {object.label} with data from {integrationName}?
        </p>

        <RadioGroup
          value={object.syncMode}
          onValueChange={(value) => {
            const mode = value as SyncMode;
            onUpdate({ syncMode: mode });
            onConfigPatch(slug, {
              allow_creates: mode === "create_and_update",
              allow_updates: true,
            });
          }}
          className="space-y-2"
        >
          <label htmlFor={`${slug}-sync-mode-create-update`} className="flex items-start gap-3 cursor-pointer">
            <RadioGroupItem id={`${slug}-sync-mode-create-update`} value="create_and_update" className="mt-0.5" />
            <div>
              <p className="text-[13px] font-medium leading-[1.35] text-[var(--mantine-color-text)]">
                Create and update
              </p>
              <p className="mt-0.5 text-[12px] leading-[1.4] text-[var(--mantine-color-dimmed)]">
                Create new {object.label} in Hyperengage for {object.crmLabel} that don't match
                an existing record, and update {object.label} for {object.crmLabel} that match.
              </p>
            </div>
          </label>

          <label htmlFor={`${slug}-sync-mode-update-only`} className="flex items-start gap-3 cursor-pointer">
            <RadioGroupItem id={`${slug}-sync-mode-update-only`} value="update_only" className="mt-0.5" />
            <div>
              <p className="text-[13px] font-medium leading-[1.35] text-[var(--mantine-color-text)]">
                Update only
              </p>
              <p className="mt-0.5 text-[12px] leading-[1.4] text-[var(--mantine-color-dimmed)]">
                Do not create new {object.label} in Hyperengage for {object.crmLabel}; only
                update existing ones.
              </p>
            </div>
          </label>
        </RadioGroup>
      </div>

      {/* ─── Section: External ID / Matcher Field ──────────────────────────── */}
      <div className="space-y-3">
        <p className="text-[13px] leading-[1.45] text-[var(--mantine-color-text)]">
          We need to be able to link your {object.crmLabel} to your Hyperengage {object.label}. Do
          your {object.crmLabel} have the{" "}
          <span className="inline-flex items-center gap-0.5 text-[var(--mantine-primary-color-filled)] cursor-pointer hover:underline">
            external ID <IconLink size={12} />
          </span>{" "}
          of the {object.label} stored in some field?
        </p>

        <RadioGroup
          value={externalIdChoice}
          onValueChange={(value) => {
            if (value === "yes") {
              onUpdate({ hasExternalId: true });
              onConfigPatch(slug, {
                matching_method: "custom_field" as any,
                hyperengage_matcher_field: "external_id",
              });
              return;
            }
            onUpdate({ hasExternalId: false, externalIdField: null });
            onConfigPatch(slug, {
              matching_method: "external_id" as any,
              matcher_field_name: undefined,
              hyperengage_matcher_field: "external_id",
            });
          }}
          className="flex items-center gap-6"
        >
          <label htmlFor={`${slug}-external-id-yes`} className="flex items-center gap-2 cursor-pointer">
            <RadioGroupItem id={`${slug}-external-id-yes`} value="yes" />
            <span className="text-[13px] leading-none text-[var(--mantine-color-text)]">Yes</span>
          </label>

          <label htmlFor={`${slug}-external-id-no`} className="flex items-center gap-2 cursor-pointer">
            <RadioGroupItem id={`${slug}-external-id-no`} value="no" />
            <span className="text-[13px] leading-none text-[var(--mantine-color-text)]">No</span>
          </label>
        </RadioGroup>

        {externalIdChoice === "yes" && (
          <div className="space-y-2">
            <p className="text-[12px] leading-[1.4] text-[var(--mantine-color-dimmed)]">
              Select the {integrationName} field that contains the external ID:
            </p>
            <Select
              value={effectiveCustomMatcherField || null}
              onChange={(val) => {
                onUpdate({ externalIdField: val });
                if (val) {
                  onConfigPatch(slug, {
                    matching_method: "custom_field" as any,
                    matcher_field_name: val,
                    hyperengage_matcher_field: "external_id",
                  });
                }
              }}
              data={
                providerFields.length > 0
                  ? providerFields
                      .filter((field) => {
                        const currentValue = object.externalIdField || config[slug]?.matcher_field_name || "";
                        if (field.key === currentValue) return true;
                        return !reservedProviderFields.has(field.key);
                      })
                      .map((f) => ({ value: f.key, label: f.title || f.key }))
                  : externalIdFields
                      .filter((field) => {
                        const currentValue = object.externalIdField || config[slug]?.matcher_field_name || "";
                        if (field.key === currentValue) return true;
                        return !reservedProviderFields.has(field.key);
                      })
                      .map((f) => ({ value: f.key, label: f.label }))
              }
              placeholder="Select field..."
              searchable
              className="max-w-[400px]"
            />
            {(object.externalIdField || config[slug]?.matcher_field_name) &&
              conflictSet.has(object.externalIdField || config[slug]?.matcher_field_name || "") && (
                <Text size="xs" c="red">
                  This field is already used in another section. Resolve duplicates before saving.
                </Text>
              )}
          </div>
        )}

        {externalIdChoice === "no" && (
          <div className="flex items-start gap-2 rounded-lg bg-[var(--mantine-color-gray-0)] px-3 py-2.5">
            <IconInfoCircle size={14} className="text-[var(--mantine-color-dimmed)] mt-0.5 flex-shrink-0" />
            <Text size="xs" c="dimmed">
              No problem. We'll use the {integrationName} Record ID to match records. This is the
              most reliable method.
            </Text>
          </div>
        )}
      </div>

      {/* ─── Section: Field Mapping ──────────────────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <p className="flex-1 text-[13px] text-foreground">
            Pick the {object.crmLabel} fields below that you want to pull in to Hyperengage and attach
            to {object.label}. Additionally, select if the trait should be editable in Hyperengage. If
            it is and a user makes an edit, Hyperengage will immediately push that change back to{" "}
            {integrationName}.
          </p>
          <DsButton
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 flex-shrink-0"
            onClick={refreshProviderFields}
            disabled={!canRefreshProviderFields || providerFieldsLoading}
            title="Refresh fields"
            aria-label="Refresh fields"
          >
            <IconRefresh size={14} className={providerFieldsLoading ? "animate-spin" : undefined} />
          </DsButton>
        </div>

        {providerFieldsLoading ? (
          <div className="flex items-center gap-2 py-4">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">Loading {integrationName} fields...</Text>
          </div>
        ) : (
          <FieldMappingTable
            slug={slug}
            mappings={draftMappings[slug] || []}
            onChange={(mappings) =>
              onMappingsChange((prev) => ({ ...prev, [slug]: mappings }))
            }
            isConnected={isConnected}
            workspaceFields={workspaceFields as FieldDefinition[] | undefined}
            hubspotFields={providerFields}
            workspaceObjectId={workspaceObjectId}
            syncId={syncId}
            connectionId={connectionId}
            matcherField={
              config[slug]?.matcher_field_name
                ? {
                    hubspot: config[slug].matcher_field_name as string,
                    hyperengage: config[slug].hyperengage_matcher_field || "external_id",
                  }
                : undefined
            }
            providerLabel={integrationName}
            providerKey={providerKey}
            hideHeader
            persistenceMode={persistenceMode}
            reservedProviderFields={reservedProviderFields}
          />
        )}
      </div>

      {/* ─── Section: Write-back ──────────────────────────────────────────────── */}
      {object.supportsWriteBack && (
        <div className="space-y-3">
          <Text size="sm">
            Once Hyperengage can establish a link between a {crmLabelSingular} and its corresponding{" "}
            {object.label.slice(0, -1)} in Hyperengage, we can attach certain data points managed in
            Hyperengage (e.g. health, segments) on to your {object.crmLabel}.
          </Text>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Text size="sm">
              {writebackEnabled
                ? `Pick the Hyperengage traits below you'd like to attach to your ${object.crmLabel}.`
                : `Enable sync-back to configure which Hyperengage traits are attached to your ${object.crmLabel}.`}
            </Text>
            <div className="flex items-center gap-2 sm:justify-end">
              <DsButton
                type="button"
                variant="outline"
                size="icon"
                className="h-7 w-7"
                onClick={refreshProviderFields}
                disabled={!canRefreshProviderFields || providerFieldsLoading}
                title="Refresh fields"
                aria-label="Refresh fields"
              >
                <IconRefresh size={12} className={providerFieldsLoading ? "animate-spin" : undefined} />
              </DsButton>
              <label className="inline-flex items-center gap-2">
                <Switch
                  checked={writebackEnabled}
                  disabled={togglingWriteback}
                  onChange={(event) => {
                    handleWritebackEnabledChange(event.currentTarget.checked).catch(console.error);
                  }}
                />
                <Text size="xs" c="dimmed">
                  Sync back
                </Text>
              </label>
            </div>
          </div>

          {writebackEnabled && !writebackReady ? (
            <div className="flex items-center gap-2 rounded-[6px] border border-[var(--mantine-color-default-border)] bg-[var(--mantine-color-gray-0)] px-3 py-2">
              <Loader size="xs" />
              <Text size="xs" c="dimmed">
                Setting up sync-back configuration...
              </Text>
            </div>
          ) : null}

          {writebackEnabled && writebackReady ? (
            <WriteBackConfiguration
              writebackConfig={writebackConfigForObject}
              onChange={(wbConfig) => {
                if (onWritebackChange) {
                  onWritebackChange((prev) => ({ ...prev, [slug]: wbConfig }));
                }
              }}
              workspaceFields={workspaceFields as FieldDefinition[] | undefined}
              hubspotFields={providerFields}
              connectionId={connectionId}
              syncId={writebackSyncId}
              slug={slug}
              providerLabel={integrationName}
              hideHeader
              persistenceMode={persistenceMode}
              managedMappings={writebackMappingsState[slug] || []}
              onManagedMappingsChange={(mappings, source) => {
                onWritebackMappingsChange?.(slug, mappings, source);
              }}
              reservedProviderFields={reservedProviderFields}
            />
          ) : null}

          {/* Lovable-style info banner */}
          <div className="border-l-[3px] border-[#8cb7ff] bg-[#edf0f7] rounded-r-lg px-4 py-3 space-y-1.5">
            <div className="flex items-start gap-2">
              <IconInfoCircle size={14} className="text-[#3b82f6] mt-0.5 flex-shrink-0" />
              <div className="text-[12px] leading-[1.5] text-[#5f6470] space-y-1">
                <p>
                  You need to create new {crmLabelSingular} fields prefixed with 'Hyperengage' in{" "}
                  {integrationName} first before you can define mappings here.{" "}
                  <button type="button" className="text-[#6b5ce7] hover:underline">
                    This article
                  </button>{" "}
                  has more details around the fields to create and which types they should be in {integrationName}.
                </p>
                <p>
                  If you'd like to overwrite an existing {integrationName} field that isn't prefixed
                  with 'Hyperengage', please{" "}
                  <button type="button" className="text-[#6b5ce7] hover:underline">
                    contact our support team
                  </button>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Role Mapping (accounts only) ───────────────────────────────────── */}
      {(slug === "accounts" || slug === "companies") && (
        <div className="space-y-4">
          {/* Lovable-style section divider */}
          <div className="flex items-center gap-3">
            <span className="text-[12px] font-medium text-[var(--mantine-color-dimmed)] uppercase tracking-wide">Role Mapping</span>
            <div className="flex-1 h-px bg-[var(--mantine-color-default-border)]" />
          </div>

          <div>
            <h3 className="text-[14px] font-semibold text-foreground">Map team member roles</h3>
            <p className="mt-1 text-[13px] text-muted-foreground">
              Connect your team roles to corresponding {integrationName} owner ID fields. This
              ensures proper account ownership across both systems.
            </p>
          </div>

          <RoleMappingTable
            slug={slug}
            roleMappings={roleMappingsState[slug] || []}
            onChange={(mappings) =>
              onRoleMappingsChange((prev) => ({ ...prev, [slug]: mappings }))
            }
            isConnected={isConnected}
            hubspotFields={providerFields}
            connectionId={connectionId}
            syncId={syncId}
            workspaceFields={workspaceFields as any[] | undefined}
            providerLabel={integrationName}
            providerKey={providerKey}
            hideHeader
            persistenceMode={persistenceMode}
            reservedProviderFields={reservedProviderFields}
            usedHubSpotFields={reservedProviderFields}
          />
        </div>
      )}

      {/* ─── Relationship Mapping (contacts only) ──────────────────────────── */}
      {slug === "contacts" && (
        <div className="space-y-4">
          {/* Lovable-style section divider */}
          <div className="flex items-center gap-3">
            <span className="text-[12px] font-medium text-[var(--mantine-color-dimmed)] uppercase tracking-wide">Relationship Mapping</span>
            <div className="flex-1 h-px bg-[var(--mantine-color-default-border)]" />
          </div>

          <div>
            <Text fw={600} size="sm">Map object relationships</Text>
            <Text size="sm" c="dimmed" mt={4}>
              Connect relationships between users and other objects. You can configure multiple
              relationship types simultaneously.
            </Text>
          </div>

          <RelationshipMappingTable
            slug={slug}
            relationshipMappings={relationshipMappingsState[slug] || []}
            onChange={(mappings) =>
              onRelationshipMappingsChange((prev) => ({ ...prev, [slug]: mappings }))
            }
            isConnected={isConnected}
            hubspotFields={providerFields}
            connectionId={connectionId}
            providerLabel={integrationName}
            hideHeader
            persistenceMode={persistenceMode}
            reservedProviderFields={reservedProviderFields}
            usedHubSpotFields={reservedProviderFields}
            enabledObjects={["accounts", "companies"].filter((candidate) => !!config[candidate]?.enabled)}
          />
        </div>
      )}
    </div>
  );
}
